import React, { useId } from 'react';

/*
 * The WannaPublish logo, rebuilt as vectors from the supplied artwork: a near-black disc with a thin orange ring and a
 * neon "W" (magenta → violet → cyan tube with a soft glow), which doubles as the first letter of the wordmark; the rest
 * of the word — "anna" + "Publish" — sits in a lozenge that shares the disc's colour and its orange outline (one
 * continuous silhouette). The badge is the same in light and dark mode (the user preferred one consistent logo); a
 * `plain` variant without the lozenge exists for places that ever need ink-on-light.
 *
 *   <WannaPublishMark size={20} />                      the disc alone (ribbon corner, favicon, title bars)
 *   <WannaPublishLockup size={44} />                    disc + wordmark; `variant` 'badge' (default), 'plain' or 'auto'
 *                                                        (lozenge in dark mode, plain in light); `pulse` breathes the glow
 *   <WannaPublishLogo size={36} /> / <WannaPublishLogoMinimal size={20} />   legacy names → lockup / mark
 */

export const LOGO_INK = '#07040d'; // disc + lozenge
export const LOGO_RING = '#ff6a2a'; // the orange outline
const NEON = ['#ff3ddc', '#c04dff', '#36d3ff']; // W, top → bottom
const PUBLISH_DARK = ['#d9c8ff', '#ff5fd6', '#a855f7']; // "Publish" on the lozenge
const PUBLISH_LIGHT = ['#6d28d9', '#db2777', '#7c3aed']; // "Publish" on a light page

const W_PATH = 'M21 29 L35 71 L50 45 L65 71 L79 29';
const RING_R = 47.3;
const RING_W = 2.6;
const DISC_R = RING_R - RING_W / 2 + 0.3; // the disc runs under the ring so no hairline shows between them

/** The neon W with its glow, drawn around (50, 50) in a 100-unit box. `pulse` makes the glow breathe (opacity only; off under prefers-reduced-motion). */
const NeonW = ({ id, glow, pulse = false }) => (
  <>
    {glow && <path d={W_PATH} stroke={`url(#${id}-w)`} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.55" filter={`url(#${id}-glow)`} className={pulse ? 'wp-neon-pulse-glow' : undefined} />}
    <path d={W_PATH} stroke={`url(#${id}-w)`} strokeWidth="6.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d={W_PATH} stroke="#ffffff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.9" className={pulse ? 'wp-neon-pulse-core' : undefined} />
  </>
);

const Defs = ({ id, glowBlur = 3 }) => (
  <defs>
    <linearGradient id={`${id}-w`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor={NEON[0]} />
      <stop offset="0.55" stopColor={NEON[1]} />
      <stop offset="1" stopColor={NEON[2]} />
    </linearGradient>
    <linearGradient id={`${id}-pd`} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor={PUBLISH_DARK[0]} />
      <stop offset="0.55" stopColor={PUBLISH_DARK[1]} />
      <stop offset="1" stopColor={PUBLISH_DARK[2]} />
    </linearGradient>
    <linearGradient id={`${id}-pl`} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor={PUBLISH_LIGHT[0]} />
      <stop offset="0.55" stopColor={PUBLISH_LIGHT[1]} />
      <stop offset="1" stopColor={PUBLISH_LIGHT[2]} />
    </linearGradient>
    <filter id={`${id}-glow`} x="-40%" y="-40%" width="180%" height="180%">
      <feGaussianBlur stdDeviation={glowBlur} />
    </filter>
  </defs>
);

/** The disc alone. */
export const WannaPublishMark = ({ size = 24, className = '', glow = true, pulse = false, title = 'WannaPublish' }) => {
  const id = useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg" data-testid="wp-logo-mark" data-pulse={pulse || undefined}>
      <Defs id={id} />
      <circle cx="50" cy="50" r={DISC_R} fill={LOGO_INK} />
      <NeonW id={id} glow={glow} pulse={pulse} />
      <circle cx="50" cy="50" r={RING_R} fill="none" stroke={LOGO_RING} strokeWidth={RING_W} />
    </svg>
  );
};

// Wordmark geometry (font-size 32 Outfit 600: "anna" = 74 u, "Publish" = 104 u — pinned with textLength so the
// lozenge always fits, whichever font has loaded).
const FONT = 'Outfit, Inter, sans-serif';
const TEXT_SIZE = 32;
const BASELINE = 61;
const ANNA_W = 74;
const PUBLISH_W = 104;
const PILL = { y: 29, h: 46, rx: 13 };
const VIEW_W = 290;

const Wordmark = ({ id, x, dark }) => (
  <>
    <text x={x} y={BASELINE} fontFamily={FONT} fontWeight="600" fontSize={TEXT_SIZE} textLength={ANNA_W} lengthAdjust="spacingAndGlyphs" fill={dark ? '#ffffff' : 'currentColor'}>anna</text>
    <text x={x + ANNA_W} y={BASELINE} fontFamily={FONT} fontWeight="600" fontSize={TEXT_SIZE} textLength={PUBLISH_W} lengthAdjust="spacingAndGlyphs" fill={`url(#${id}-${dark ? 'pd' : 'pl'})`}>Publish</text>
  </>
);

/** Disc + lozenge as one silhouette with one orange outline (the strokes are masked where they run under each other). */
const BadgeLockup = ({ id, glow, pulse }) => {
  const textX = 87; // inside the disc, right after the W — the lozenge continues the disc's colour so the word reads as one
  const pillRight = 284;
  const pillW = pillRight - 50;
  return (
    <>
      <mask id={`${id}-mring`} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW_W} height="100">
        <rect x="0" y="0" width={VIEW_W} height="100" fill="#fff" />
        <rect x="50" y={PILL.y} width={pillW} height={PILL.h} rx={PILL.rx} fill="#000" />
      </mask>
      <mask id={`${id}-mpill`} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW_W} height="100">
        <rect x="0" y="0" width={VIEW_W} height="100" fill="#fff" />
        <circle cx="50" cy="50" r={RING_R} fill="#000" />
      </mask>
      <rect x="50" y={PILL.y} width={pillW} height={PILL.h} rx={PILL.rx} fill={LOGO_INK} />
      <circle cx="50" cy="50" r={DISC_R} fill={LOGO_INK} />
      <Wordmark id={id} x={textX} dark />
      <NeonW id={id} glow={glow} pulse={pulse} />
      <rect x="50" y={PILL.y} width={pillW} height={PILL.h} rx={PILL.rx} fill="none" stroke={LOGO_RING} strokeWidth={RING_W} mask={`url(#${id}-mpill)`} />
      <circle cx="50" cy="50" r={RING_R} fill="none" stroke={LOGO_RING} strokeWidth={RING_W} mask={`url(#${id}-mring)`} />
    </>
  );
};

/** Disc + "annaPublish" in ink, no lozenge (light pages). */
const PlainLockup = ({ id, glow, pulse }) => (
  <>
    <circle cx="50" cy="50" r={DISC_R} fill={LOGO_INK} />
    <NeonW id={id} glow={glow} pulse={pulse} />
    <circle cx="50" cy="50" r={RING_R} fill="none" stroke={LOGO_RING} strokeWidth={RING_W} />
    <Wordmark id={id} x={104} dark={false} />
  </>
);

/**
 * The full logo. `size` is the disc's diameter in px (the lockup is 2.9× as wide). `variant`: 'badge' (default — the same
 * in light and dark), 'plain' (no lozenge, ink wordmark), or 'auto' (lozenge in dark mode — detected through the `.dark`
 * class on any ancestor — plain in light). `pulse` breathes the W's glow (loading screens).
 */
export const WannaPublishLockup = ({ size = 44, className = '', variant = 'badge', glow = true, pulse = false, title = 'WannaPublish' }) => {
  const id = useId().replace(/:/g, '');
  const width = Math.round((size * VIEW_W) / 100);
  return (
    <svg width={width} height={size} viewBox={`0 0 ${VIEW_W} 100`} className={`text-foreground ${className}`} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg" data-testid="wp-logo-lockup" data-variant={variant} data-pulse={pulse || undefined}>
      <Defs id={id} />
      {variant !== 'plain' && <g className={variant === 'auto' ? 'hidden dark:block' : undefined}><BadgeLockup id={id} glow={glow} pulse={pulse} /></g>}
      {variant !== 'badge' && <g className={variant === 'auto' ? 'dark:hidden' : undefined}><PlainLockup id={id} glow={glow} pulse={pulse} /></g>}
    </svg>
  );
};

// Legacy names, kept so existing call sites keep working.
export const WannaPublishLogo = ({ size = 32, className = '', ...rest }) => <WannaPublishLockup size={size} className={className} {...rest} />;
export const WannaPublishLogoMinimal = ({ size = 24, className = '', ...rest }) => <WannaPublishMark size={size} className={className} {...rest} />;
